import React, { useCallback, useMemo, useState } from 'react';
import ReactFlow, {
  Node,
  Edge,
  Controls,
  Background,
  BackgroundVariant,
  MiniMap,
  useNodesState,
  useEdgesState,
  Connection,
  addEdge,
  MarkerType,
  Position,
  Handle,
  useReactFlow,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { PhoneCall, Hash, Mic2, Mail, PhoneOff, PlayCircle, Zap, Code, Plus, Trash2, Users } from 'lucide-react';

interface IVRNode {
  id: string;
  // ⚠ A LOCAL redeclaration of the node type. types/ivr.ts owns IVRNodeType,
  // and this list has to be kept in step by hand — adding 'queue' meant
  // editing it in three places before the build went green. Worth collapsing
  // onto the shared type; until then, edit all three.
  type: 'greeting' | 'menu' | 'input' | 'transfer' | 'voicemail' | 'hangup' | 'ai_agent' | 'api_call' | 'queue' | 'twiml';
  label: string;
  message: string;
  options?: { digit: string; label: string; nextNode: string }[];
  nextNode?: string;
  transferNumber?: string;
  aiAgent?: string;
  timeout?: number;
}

/** Parsed identity of a flow edge, so a canvas disconnect maps back to the model. */
export interface EdgeRef {
  sourceId: string;
  targetId: string;
  kind: 'option' | 'next';
  optionIndex?: number;
}

interface GraphFlowDiagramProps {
  nodes: IVRNode[];
  currentNodeId: string;
  onNodeClick?: (node: IVRNode) => void;
  onAddNode?: (type: IVRNode['type'], position?: { x: number; y: number }) => void;
  onUpdateConnection?: (sourceId: string, targetId: string, optionIndex?: number) => void;
  onDisconnect?: (ref: EdgeRef) => void;
  onDeleteNode?: (nodeId: string) => void;
}

// Custom Node Component
const CustomNode = ({ data }: { data: any }) => {
  const { node, isActive, onClick, onDelete, canDelete } = data;
  const style = getNodeStyle(node.type);
  const Icon = style.icon;

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onDelete) {
      onDelete(node.id);
    }
  };

  return (
    <div
      onClick={() => onClick?.(node)}
      className={`bg-white rounded-xl shadow-lg border-2 relative ${
        isActive ? 'border-green-500 shadow-green-200' : style.border
      } ${style.shadow} hover:shadow-xl transition-all cursor-pointer`}
      style={{ width: '280px', minHeight: '160px' }}
    >
      {/* Connection anchors — target on top (incoming), source on bottom
          (outgoing). Drag from the bottom handle to another node's top handle
          to create an edge (mapped to options/nextNode by the parent).
          stopPropagation keeps a handle click/drag from bubbling to the node's
          onClick, which would otherwise switch to the Flow Design tab and abort
          the connection gesture. React Flow's own drag handler still fires. */}
      <Handle
        type="target"
        position={Position.Top}
        id="in"
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        style={{ background: 'var(--text-muted)', width: 12, height: 12, cursor: 'crosshair' }}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        id="out"
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        style={{ background: 'var(--text-muted)', width: 12, height: 12, cursor: 'crosshair' }}
      />

      {/* Header */}
      <div className={`${style.fill} text-white px-4 py-3 rounded-t-xl flex items-center justify-between`}>
        <div className="flex items-center gap-2">
          <div className="bg-white bg-opacity-30 p-1.5 rounded-lg">
            <Icon className="w-5 h-5" />
          </div>
          <span className="font-semibold text-sm truncate max-w-[150px]">{node.label}</span>
        </div>
        <div className="bg-white bg-opacity-30 px-2 py-0.5 rounded-full">
          <span className="text-[10px] font-semibold uppercase">{node.type.replace('_', ' ')}</span>
        </div>
      </div>

      {/* Content */}
      <div className="p-3 text-xs">
        {node.message && (
          <div className="mb-2">
            <p className="text-gray-600 font-semibold mb-1">Message:</p>
            <p className="text-gray-800 line-clamp-3 leading-tight">{node.message}</p>
          </div>
        )}

        {node.type === 'menu' && node.options && node.options.length > 0 && (
          <div className="space-y-1">
            {node.options.slice(0, 3).map((opt: { digit: string; label: string; nextNode: string }, idx: number) => (
              <div key={idx} className="flex items-center gap-2">
                <span className="bg-blue-600 text-white w-5 h-5 rounded flex items-center justify-center font-bold text-[10px]">
                  {opt.digit}
                </span>
                <span className="text-gray-700 truncate">{opt.label}</span>
              </div>
            ))}
            {node.options.length > 3 && (
              <p className="text-gray-500 text-[10px] mt-1">+{node.options.length - 3} more options</p>
            )}
          </div>
        )}

        {node.type === 'transfer' && node.transferNumber && (
          <div>
            <p className="text-gray-600 font-semibold">Transfer to:</p>
            <p className="text-gray-800 font-mono">{node.transferNumber}</p>
          </div>
        )}

        {node.type === 'ai_agent' && node.aiAgent && (
          <div>
            <p className="text-gray-600 font-semibold">AI Agent:</p>
            <p className="text-gray-800">{node.aiAgent}</p>
          </div>
        )}
      </div>

      {/* Active Badge */}
      {isActive && (
        <div className="absolute -top-2 -right-2 bg-green-500 text-white px-2 py-1 rounded-full text-[10px] font-bold shadow-lg">
          ACTIVE
        </div>
      )}

      {/* Delete Button */}
      {canDelete && onDelete && (
        <button
          onClick={handleDelete}
          className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white p-1.5 rounded-lg shadow-lg transition-all hover:scale-110 z-10"
          title="Delete node"
        >
          <Trash2 className="w-3 h-3" />
        </button>
      )}
    </div>
  );
};

// Get icon and color for each node type
const getNodeStyle = (type: IVRNode['type']) => {
  const styles = {
    greeting: {
      icon: PhoneCall,
      fill: 'bg-green-600',
      border: 'border-green-500',
      shadow: 'shadow-green-200',
      text: 'text-green-700',
      bg: 'bg-green-50',
      color: '#639922',
    },
    menu: {
      icon: Hash,
      fill: 'bg-blue-600',
      border: 'border-blue-500',
      shadow: 'shadow-blue-200',
      text: 'text-blue-700',
      bg: 'bg-blue-50',
      color: '#D85A30',
    },
    input: {
      icon: Mic2,
      fill: 'bg-purple-600',
      border: 'border-purple-500',
      shadow: 'shadow-purple-200',
      text: 'text-purple-700',
      bg: 'bg-purple-50',
      color: '#854762',
    },
    transfer: {
      icon: PhoneCall,
      fill: 'bg-orange-600',
      border: 'border-orange-500',
      shadow: 'shadow-orange-200',
      text: 'text-orange-700',
      bg: 'bg-orange-50',
      color: '#C96424',
    },
    voicemail: {
      icon: Mail,
      fill: 'bg-pink-600',
      border: 'border-pink-500',
      shadow: 'shadow-pink-200',
      text: 'text-pink-700',
      bg: 'bg-pink-50',
      color: '#A2617C',
    },
    hangup: {
      icon: PhoneOff,
      fill: 'bg-red-600',
      border: 'border-red-500',
      shadow: 'shadow-red-200',
      text: 'text-red-700',
      bg: 'bg-red-50',
      color: '#C94A4A',
    },
    ai_agent: {
      icon: PlayCircle,
      fill: 'bg-indigo-600',
      border: 'border-indigo-500',
      shadow: 'shadow-indigo-200',
      text: 'text-indigo-700',
      bg: 'bg-indigo-50',
      color: '#9A5230',
    },
    api_call: {
      icon: Zap,
      fill: 'bg-yellow-600',
      border: 'border-yellow-500',
      shadow: 'shadow-yellow-200',
      text: 'text-yellow-700',
      bg: 'bg-yellow-50',
      color: '#C4780F',
    },
    queue: {
      icon: Users,
      fill: 'bg-teal-600',
      border: 'border-teal-500',
      shadow: 'shadow-teal-200',
      text: 'text-teal-700',
      bg: 'bg-teal-50',
      color: '#0d9488',
    },
    twiml: {
      icon: Code,
      fill: 'bg-gray-600',
      border: 'border-gray-500',
      shadow: 'shadow-gray-200',
      text: 'text-gray-700',
      bg: 'bg-gray-50',
      color: '#888780',
    },
  };
  return styles[type];
};

/** Reverse of the edge-id encoding above — turns a canvas edge back into the
 *  model field it represents so a disconnect can clear the right thing. */
const parseEdgeId = (edge: Edge): EdgeRef | null => {
  const id = edge.id || '';
  const optMatch = id.match(/^(.+)::opt:(\d+)->(.+)$/);
  if (optMatch) return { sourceId: optMatch[1], optionIndex: Number(optMatch[2]), targetId: optMatch[3], kind: 'option' };
  const nextMatch = id.match(/^(.+)::next->(.+)$/);
  if (nextMatch) return { sourceId: nextMatch[1], targetId: nextMatch[2], kind: 'next' };
  // Fallback for edges created live by React Flow (before model round-trips).
  if (edge.source && edge.target) return { sourceId: edge.source, targetId: edge.target, kind: 'next' };
  return null;
};

const GraphFlowDiagram: React.FC<GraphFlowDiagramProps> = ({
  nodes: ivrNodes,
  currentNodeId,
  onNodeClick,
  onAddNode,
  onUpdateConnection,
  onDisconnect,
  onDeleteNode
}) => {
  // Convert IVR nodes to React Flow nodes
  const { nodes: flowNodes, edges: flowEdges } = useMemo(() => {
    const nodeMap = new Map(ivrNodes.map(n => [n.id, n]));
    const visited = new Set<string>();
    const levels: string[][] = [];

    // Build hierarchical tree structure
    const buildLevels = (nodeId: string, level: number) => {
      if (visited.has(nodeId)) return;
      visited.add(nodeId);

      if (!levels[level]) levels[level] = [];
      levels[level].push(nodeId);

      const node = nodeMap.get(nodeId);
      if (!node) return;

      // Follow both edge kinds: menu options and the single nextNode hop.
      if (node.options) {
        node.options.forEach(opt => {
          if (opt.nextNode) buildLevels(opt.nextNode, level + 1);
        });
      }
      if (node.nextNode) buildLevels(node.nextNode, level + 1);
    };

    // Start from first node
    if (ivrNodes.length > 0) {
      buildLevels(ivrNodes[0].id, 0);
    }

    // Add orphaned nodes
    ivrNodes.forEach(node => {
      if (!visited.has(node.id)) {
        if (!levels[levels.length]) levels[levels.length] = [];
        levels[levels.length - 1].push(node.id);
      }
    });

    // Convert to React Flow nodes
    const flowNodes: Node[] = [];
    const NODE_WIDTH = 320;
    const NODE_HEIGHT = 200;
    const HORIZONTAL_GAP = 150;
    const VERTICAL_GAP = 200;

    levels.forEach((levelNodes, levelIndex) => {
      const levelWidth = levelNodes.length * NODE_WIDTH + (levelNodes.length - 1) * HORIZONTAL_GAP;
      const startX = -levelWidth / 2;

      levelNodes.forEach((nodeId, index) => {
        const node = nodeMap.get(nodeId);
        if (!node) return;

        const x = startX + index * (NODE_WIDTH + HORIZONTAL_GAP);
        const y = levelIndex * (NODE_HEIGHT + VERTICAL_GAP);

        flowNodes.push({
          id: nodeId,
          type: 'customNode',
          position: { x, y },
          data: {
            node,
            isActive: node.id === currentNodeId,
            onClick: onNodeClick,
            onDelete: onDeleteNode,
            canDelete: node.id !== 'start', // Can't delete the start node
          },
          sourcePosition: Position.Bottom,
          targetPosition: Position.Top,
        });
      });
    });

    // Convert to React Flow edges. Edge ids are PARSEABLE so a canvas
    // disconnect maps back to the exact model field (see parseEdgeId):
    //   option edge: `${source}::opt:${index}->${target}`
    //   next edge:   `${source}::next->${target}`
    const flowEdges: Edge[] = [];
    ivrNodes.forEach(node => {
      const style = getNodeStyle(node.type);
      const baseLabel = {
        labelStyle: { fill: style.color, fontWeight: 600, fontSize: 12 },
        labelBgStyle: { fill: 'white', fillOpacity: 0.9 },
        markerEnd: { type: MarkerType.ArrowClosed, color: style.color },
        animated: node.id === currentNodeId,
        sourceHandle: 'out',
        targetHandle: 'in',
      } as const;

      // Menu option edges (choices[].nextState).
      (node.options || []).forEach((opt, idx) => {
        if (!opt.nextNode) return;
        flowEdges.push({
          ...baseLabel,
          id: `${node.id}::opt:${idx}->${opt.nextNode}`,
          source: node.id,
          target: opt.nextNode,
          label: opt.digit ? `Press ${opt.digit}` : 'option',
          type: 'smoothstep',
          style: { stroke: style.color, strokeWidth: 2 },
        });
      });

      // Single nextNode hop (input.nextState) — dashed to distinguish it.
      if (node.nextNode) {
        flowEdges.push({
          ...baseLabel,
          id: `${node.id}::next->${node.nextNode}`,
          source: node.id,
          target: node.nextNode,
          label: 'next',
          type: 'smoothstep',
          style: { stroke: style.color, strokeWidth: 2, strokeDasharray: '6 4' },
        });
      }
    });

    return { nodes: flowNodes, edges: flowEdges };
  }, [ivrNodes, currentNodeId, onNodeClick, onDeleteNode]);

  const [nodes, setNodes, onNodesChange] = useNodesState(flowNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(flowEdges);

  // Update nodes when IVR nodes change
  React.useEffect(() => {
    setNodes(flowNodes);
    setEdges(flowEdges);
  }, [flowNodes, flowEdges, setNodes, setEdges]);

  const nodeTypes = useMemo(() => ({ customNode: CustomNode }), []);

  const onConnect = useCallback(
    (params: Connection) => {
      // Don't add the edge to local state directly — let the parent update the
      // IVR model (options/nextNode); the edges then rebuild from that single
      // source of truth via the effect below. Avoids duplicate/ghost edges.
      if (params.source && params.target && params.source !== params.target && onUpdateConnection) {
        onUpdateConnection(params.source, params.target);
      }
    },
    [onUpdateConnection]
  );

  // Deleting an edge on the canvas clears the matching model field.
  const onEdgesDelete = useCallback(
    (deleted: Edge[]) => {
      if (!onDisconnect) return;
      deleted.forEach((e) => {
        const ref = parseEdgeId(e);
        if (ref) onDisconnect(ref);
      });
    },
    [onDisconnect]
  );

  const handlePaneClick = useCallback(
    (event: React.MouseEvent) => {
      // Show context menu for adding nodes
      console.log('Pane clicked at:', event.clientX, event.clientY);
    },
    []
  );

  // Handle node deletion with keyboard (Delete or Backspace)
  const handleNodesDelete = useCallback(
    (nodesToDelete: Node[]) => {
      if (onDeleteNode) {
        nodesToDelete.forEach(node => {
          const ivrNode = ivrNodes.find(n => n.id === node.id);
          // Don't delete the start node
          if (ivrNode && ivrNode.id !== 'start') {
            onDeleteNode(node.id);
          }
        });
      }
    },
    [onDeleteNode, ivrNodes]
  );

  if (ivrNodes.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
        <PhoneCall className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-600 font-medium">No nodes in the flow yet</p>
        <p className="text-gray-500 text-sm mt-1">Add nodes from the left panel to build your IVR flow</p>
      </div>
    );
  }

  return (
    <div className="bg-slate-50 rounded-lg border-2 border-slate-200 overflow-hidden">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <PhoneCall className="w-5 h-5 text-green-600" />
              IVR Flow Diagram
            </h3>
            <p className="text-sm text-gray-600 mt-0.5">
              Interactive call flow visualization • Drag nodes • Scroll to zoom • Click to edit
            </p>
          </div>
          <div className="text-sm text-gray-600">
            <span className="font-medium">{ivrNodes.length}</span> nodes
          </div>
        </div>

        {/* Node Toolbar */}
        {onAddNode && (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-medium text-gray-700 mr-2">Add Node:</span>
            {[
              { type: 'greeting' as const, label: 'Greeting', icon: PhoneCall },
              { type: 'menu' as const, label: 'Menu', icon: Hash },
              { type: 'input' as const, label: 'Input', icon: Mic2 },
              { type: 'transfer' as const, label: 'Transfer', icon: PhoneCall },
              { type: 'ai_agent' as const, label: 'AI Agent', icon: PlayCircle },
              { type: 'api_call' as const, label: 'API Call', icon: Zap },
              // Ring group: [acd_queue] dispatches queue_<ext> into the org's own
              // internal context. Live on the switch all along.
              { type: 'queue' as const, label: 'Ring Group', icon: Users },
              // TwiML withdrawn 2026-08-31 — no TwiML runtime exists and the body
              // was never deployed. Removed from BOTH palettes: this one and
              // IVRCreation.tsx. Editing one and not the other is how a withdrawn
              // node stayed on offer on whichever view you happened to open.
              { type: 'voicemail' as const, label: 'Voicemail', icon: Mail },
              { type: 'hangup' as const, label: 'Hangup', icon: PhoneOff },
            ].map((nodeType) => {
              const Icon = nodeType.icon;
              const style = getNodeStyle(nodeType.type);
              return (
                <button
                  key={nodeType.type}
                  onClick={() => onAddNode(nodeType.type)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border-2 rounded-lg transition hover:shadow-md ${style.bg} ${style.border} ${style.text} hover:scale-105`}
                  title={`Add ${nodeType.label} node`}
                >
                  <Plus className="w-3 h-3" />
                  <Icon className="w-3 h-3" />
                  <span>{nodeType.label}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* React Flow Canvas */}
      <div style={{ height: '700px' }}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onNodesDelete={handleNodesDelete}
          onEdgesDelete={onEdgesDelete}
          nodeTypes={nodeTypes}
          fitView
          fitViewOptions={{ padding: 0.2 }}
          minZoom={0.1}
          maxZoom={2}
          deleteKeyCode={['Backspace', 'Delete']}
          defaultEdgeOptions={{
            type: 'smoothstep',
            animated: false,
          }}
        >
          <Controls />
          <MiniMap
            nodeColor={(node) => {
              const ivrNode = ivrNodes.find(n => n.id === node.id);
              if (!ivrNode) return 'var(--border)';
              const style = getNodeStyle(ivrNode.type);
              return style.color;
            }}
            maskColor="rgba(0, 0, 0, 0.1)"
            style={{ backgroundColor: 'var(--surface-raised)' }}
          />
          <Background variant={BackgroundVariant.Dots} gap={16} size={1} color="var(--border)" />
        </ReactFlow>
      </div>

      {/* Legend */}
      <div className="bg-white border-t border-slate-200 px-6 py-4">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-2 text-xs">
            <span className="font-semibold text-gray-700">Legend:</span>
            {[
              { type: 'greeting' as const, label: 'Greeting' },
              { type: 'menu' as const, label: 'Menu' },
              { type: 'transfer' as const, label: 'Transfer' },
              { type: 'ai_agent' as const, label: 'AI Agent' },
              { type: 'hangup' as const, label: 'Hangup' },
            ].map(({ type, label }) => {
              const style = getNodeStyle(type);
              const Icon = style.icon;
              return (
                <div key={type} className="flex items-center gap-1.5">
                  <div className={`w-6 h-6 rounded flex items-center justify-center ${style.fill}`}>
                    <Icon className="w-3 h-3 text-white" />
                  </div>
                  <span className="text-gray-600">{label}</span>
                </div>
              );
            })}
          </div>
          <div className="text-xs text-gray-600">
            💡 Drag nodes to rearrange • Click nodes to edit • Press Delete or click 🗑️ to remove nodes
          </div>
        </div>
      </div>
    </div>
  );
};

export default GraphFlowDiagram;
